import jwt from 'jsonwebtoken'

// Exact signed claim namespace stamped by the Auth0 Post-Login Action. Pin
// this literal — a typo silently disables MFA enforcement.
export const MFA_REQUIRED_CLAIM = 'https://importgenius.com/mfa_required'

// fingerprint_source tag the Auth0 Action expects for Lighthouse logins.
// Matches the login() call in src/pages/login.vue.
export const MFA_SOURCE = 'lighthouse'

// rewrite-api endpoint (relative to IMPORTSCAN_AWS_URL) that confirms whether
// MFA enrollment is still required.
export const MFA_REQUIREMENT_PATH = '/api/mfa/requirement'

// Ceiling on the boot-blocking requirement check. There is no global axios
// timeout, so without this a hanging endpoint would stall first paint forever
// (the middleware parks boot after the fail-closed redirect). On timeout the
// call rejects and fails closed fast.
export const MFA_REQUIREMENT_TIMEOUT_MS = 5000

// Per-tab cache of a confirmed `required: false` verdict so a stale-positive
// claim that survives on the token is not re-confirmed against the requirement
// endpoint on every navigation. Keyed to the token's signature segment (unique
// per issuance), so a re-minted token re-verifies instead of riding an old
// verdict. Only a real released verdict is ever stored — never the fail-closed
// default — so the cache can only skip-to-proceed and always fails safe.
// Session-scoped: it clears with the tab.
export const MFA_RELEASED_KEY = 'isng.mfa.released-token'

/**
 * Reads the forced-MFA claim from an id_token.
 *
 * @param {string} token - The raw id_token, optionally 'Bearer '-prefixed.
 * @returns {boolean} true only when the claim is strictly boolean true.
 */
export function readMfaRequiredClaim(token) {
  if (!token) {
    return false
  }

  const decoded = jwt.decode(token.replace('Bearer ', ''))

  return decoded !== null && decoded[MFA_REQUIRED_CLAIM] === true
}

/**
 * Builds the ig-console enrollment URL to hand a flagged user off to.
 *
 * @returns {string|undefined} The console security URL, or undefined when
 *   CONSOLE_APP_URL is not configured.
 */
export function buildMfaEnrollmentUrl() {
  const consoleAppUrl = process.env.CONSOLE_APP_URL

  if (!consoleAppUrl) {
    console.error('[mfa] CONSOLE_APP_URL is not configured')
    return undefined
  }

  return `${consoleAppUrl}/account/security?mfa_required=1&source=${MFA_SOURCE}`
}

/**
 * Confirms with rewrite-api whether MFA enrollment is still required,
 * neutralising a stale-positive claim. Fails CLOSED: any non-2xx or thrown
 * error is treated as still required, so a check outage can never bypass MFA.
 *
 * @param {object} $axios - The Nuxt axios instance (JWT attached globally).
 * @returns {Promise<boolean>} false only on an explicit { required: false }.
 */
export async function confirmMfaRequirement($axios) {
  try {
    const { data } = await $axios.get(
      `${process.env.IMPORTSCAN_AWS_URL}${MFA_REQUIREMENT_PATH}`,
      {
        progress: false,
        // A backend blip must fail closed fast, not stall boot: cap the wait and
        // opt out of the global axios-retry so there is no exponential backoff
        // before the fail-closed redirect fires.
        timeout: MFA_REQUIREMENT_TIMEOUT_MS,
        'axios-retry': { retries: 0 }
      }
    )

    return data.required !== false
  } catch (error) {
    // Fail closed, but leave a trace: on a rewrite-api outage every flagged
    // user is bounced to enrollment, so log it to keep the incident findable.
    console.error('[mfa] requirement check failed', error)
    return true
  }
}

/**
 * Whether the id_token cannot back a gated MFA check — absent, undecodable,
 * missing exp, or past exp. An expired session belongs in the login flow, not
 * the MFA flow: the requirement call would 401 into a fail-closed console
 * redirect the middleware's chain-halt makes inescapable (boot parks on it).
 *
 * @param {string} token - The id_token, optionally 'Bearer '-prefixed.
 * @returns {boolean} true when the token is unusable for a gated check.
 */
export function isMfaTokenExpired(token) {
  if (!token) {
    return true
  }

  const decoded = jwt.decode(token.replace('Bearer ', ''))

  if (!decoded || typeof decoded.exp !== 'number') {
    return true
  }

  return decoded.exp < Date.now() / 1000
}

/**
 * The JWT signature segment — a compact per-token fingerprint. Empty when the
 * token is not a well-formed three-segment JWT.
 *
 * @param {string} token - The id_token, optionally 'Bearer '-prefixed.
 * @returns {string} The signature segment, or '' when unavailable.
 */
export function mfaTokenFingerprint(token) {
  if (!token) {
    return ''
  }

  return token.replace('Bearer ', '').split('.')[2] || ''
}

/**
 * Whether this tab already confirmed MFA released for this exact token.
 *
 * @param {string} token - The id_token, optionally 'Bearer '-prefixed.
 * @returns {boolean} true only when a stored verdict matches the token.
 */
export function isMfaReleased(token) {
  const fingerprint = mfaTokenFingerprint(token)

  if (!fingerprint) {
    return false
  }

  try {
    return sessionStorage.getItem(MFA_RELEASED_KEY) === fingerprint
  } catch (_error) {
    // A blocked/unavailable sessionStorage just costs a repeat check — safe.
    return false
  }
}

/**
 * Records a confirmed released verdict for this token so later navigations in
 * this tab skip the requirement call.
 *
 * @param {string} token - The id_token, optionally 'Bearer '-prefixed.
 */
export function rememberMfaReleased(token) {
  const fingerprint = mfaTokenFingerprint(token)

  if (!fingerprint) {
    return
  }

  try {
    sessionStorage.setItem(MFA_RELEASED_KEY, fingerprint)
  } catch (_error) {
    // Ignore: a lost cache only costs a repeat requirement call.
  }
}
